import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { useState } from "react";
import { tenantIDAtom } from "../../../atoms/GlobalAtoms";
import { callFunctionIfExists } from "../../../utils/GeneralUtils";
import { newToastEvent } from "../../../utils/ToastUtil";
import TenantAPI from "../../api/TenantAPI";
import { FormBuilder } from "../../../types/FormBuilderInterfaces";

const useUpdatePublicForm = () => {
  const queryClient = useQueryClient();
  const tenantID = useAtomValue(tenantIDAtom);
  const [updateID, setUpdateID] = useState<string | null>(null);

  const updatePublicForm = useMutation({
    mutationFn: async ({
      updatedPublicForm,
    }: {
      updatedPublicForm: FormBuilder;
      onSuccess?: (formID: string) => void;
    }) => {
      if (!updatedPublicForm._id) return;
      setUpdateID(updatedPublicForm?._id);
      return TenantAPI.updatePublicForm(updatedPublicForm);
    },
    onSuccess: (response, { updatedPublicForm, onSuccess }) => {
      const updatedPF = response?.data.data.publicForm;

      queryClient.setQueryData(["publicForms", tenantID], (prev: any) => {
        if (!Array.isArray(prev)) return prev;

        const updatedList = prev.map((pf) =>
          pf.id === updatedPublicForm._id ? updatedPF : pf
        );

        return updatedList;
      });

      newToastEvent(
        "success",
        `Successfully updated public form "${updatedPublicForm.ModuleAPIName}".`
      );

      callFunctionIfExists(onSuccess, updatedPF.id);
    },
    onError: (err) => {
      const errorMessage =
        "You don't have permissions to update a public form.";
      newToastEvent("error", errorMessage);
    },
    onSettled: () => setUpdateID(null),
  });

  return {
    updatePublicForm: updatePublicForm.mutate,
    updateID: updateID,
    updatePublicFormError: updatePublicForm?.isError,
  };
};

export default useUpdatePublicForm;
